/**
 * The PIN screens: entering one to get in, and setting one up.
 *
 * The escape hatch is on the screen, always. A lock whose only visible action
 * is "enter your PIN" turns a forgotten PIN into a support call; "Use the
 * passphrase instead" turns it into ten seconds of mild annoyance.
 */
import { useEffect, useRef, useState } from "react";

import { Card, buttonClass, inputClass } from "../components/ui.js";
import { Wordmark } from "../components/Logo.js";
import { MAX_ATTEMPTS, MIN_PIN_LENGTH, setLock, unlockWithPin } from "../lock.js";

const pinInputClass = `${inputClass} text-center text-2xl tracking-[0.4em]`;

export function PinPrompt({
  attemptsLeft,
  onUnlocked,
  onUsePassphrase,
  onWiped,
}: {
  attemptsLeft: number;
  onUnlocked: (passphrase: string) => void;
  onUsePassphrase: () => void;
  onWiped: () => void;
}) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [left, setLeft] = useState(attemptsLeft);
  const field = useRef<HTMLInputElement>(null);

  useEffect(() => field.current?.focus(), []);

  async function submit() {
    if (!pin || busy) return;
    setBusy(true);
    setError("");
    try {
      const result = await unlockWithPin(pin);
      if (result.ok) {
        onUnlocked(result.passphrase);
        return;
      }
      setPin("");
      if (result.reason === "wrong") {
        setLeft(result.attemptsLeft);
        setError(
          result.attemptsLeft === 1
            ? "Wrong PIN. One more try before it is removed."
            : `Wrong PIN. ${result.attemptsLeft} tries left.`,
        );
      } else {
        // Wiped or absent — either way there is nothing to enter a PIN into.
        onWiped();
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-4 p-4">
      <Wordmark size={30} />
      <h1 className="text-xl font-semibold">Enter your PIN</h1>

      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <input
          ref={field}
          className={pinInputClass}
          // `numeric` and not `tel`: tel offers a dialler keypad with symbols
          // this field will never accept.
          inputMode="numeric"
          autoComplete="off"
          type="password"
          maxLength={12}
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
        />
        <button type="submit" className={buttonClass} disabled={!pin || busy}>
          {busy ? "Checking…" : "Unlock"}
        </button>
      </form>

      {error && <p className="text-sm text-bad">{error}</p>}
      {!error && left < MAX_ATTEMPTS && (
        <p className="text-xs text-ink-3">{left} tries left before the PIN is removed.</p>
      )}

      <button type="button" className="text-sm font-medium text-accent" onClick={onUsePassphrase}>
        Use the passphrase instead
      </button>
    </div>
  );
}

export function PinSetup({
  passphrase,
  onDone,
  onCancel,
}: {
  passphrase: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [pin, setPin] = useState("");
  const [again, setAgain] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const tooShort = pin.length > 0 && pin.length < MIN_PIN_LENGTH;
  const mismatch = again.length > 0 && again !== pin;
  const ready = pin.length >= MIN_PIN_LENGTH && again === pin && !busy;

  async function save() {
    setBusy(true);
    setError("");
    try {
      await setLock(pin, passphrase, "pin");
      onDone();
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : String(problem));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <Card title="Set a PIN">
        <p className="mb-4 text-sm text-ink-2">
          Your passphrase is stored on this phone, encrypted with this PIN. It replaces typing the
          passphrase each time — it does not replace the passphrase itself, which still works and is
          the way back in if you forget this.
        </p>

        <div className="space-y-3">
          <input
            className={pinInputClass}
            inputMode="numeric"
            type="password"
            maxLength={12}
            placeholder="PIN"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
          />
          <input
            className={pinInputClass}
            inputMode="numeric"
            type="password"
            maxLength={12}
            placeholder="Again"
            value={again}
            onChange={(e) => setAgain(e.target.value.replace(/\D/g, ""))}
          />

          {tooShort && (
            <p className="text-xs text-warn">At least {MIN_PIN_LENGTH} digits.</p>
          )}
          {mismatch && <p className="text-xs text-warn">The two PINs do not match.</p>}
          {error && <p className="text-xs text-bad">{error}</p>}

          <p className="text-xs text-ink-3">
            After {MAX_ATTEMPTS} wrong attempts the stored passphrase is deleted from this phone and
            you unlock with the passphrase again. Nothing is lost.
          </p>

          <button type="button" className={buttonClass} disabled={!ready} onClick={() => void save()}>
            {busy ? "Saving…" : "Save PIN"}
          </button>
          <button type="button" className="w-full py-2 text-sm text-ink-3" onClick={onCancel}>
            Not now
          </button>
        </div>
      </Card>
    </div>
  );
}
